import { useCallback, useEffect, useState } from "react";
import api from "../../../../../services/api";
import { formatDate as formatDateDisplay } from "../../../../../utils/formatDate";
import { notifyError, notifySucces } from "../../../../../utils/notifications";
import {
  Elegible,
  TElegibleAntecipation,
  TErrorElegileAntecipation,
  TUseAntecipateSalesModal,
} from "./types";

const useAntecipateSalesModal = ({
  clientID,
  isVisible,
  handleForceUpdate,
  onClose,
}: TUseAntecipateSalesModal) => {
  const [selectedSaleId, setSelectedSaleId] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [salesAntecipateData, setSalesAntecipateData] = useState<
    TElegibleAntecipation[]
  >([] as TElegibleAntecipation[]);
  const [fetchingData, setFetchingData] = useState<boolean>(false);
  const [type, setType] = useState<"partial" | "total">("partial");
  const [modalConfirmSales, setModalConfirmSales] = useState<boolean>(false);
  const [installmentsToAntecipate, setInstallmentsToAntecipate] =
    useState<number>(1);

  const fetchElegiblePurchase = useCallback(async () => {
    const today = new Date();
    const past = new Date();
    past.setDate(today.getDate() - 30);

    const initialDate = past.toISOString().split("T")[0];
    const finalDate = today.toISOString().split("T")[0];

    const params = {
      idPortador: clientID,
      initialDate,
      finalDate,
    };

    const query = new URLSearchParams(
      Object.entries(params).reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key]: String(value),
        }),
        {}
      )
    ).toString();

    try {
      setFetchingData(true);
      const response = await api.get(
        `elegible_advance_purchase?${query}`,
        {},
        {},
        false,
        true
      );
      setSalesAntecipateData(response.data.content);
    } catch (error) {
      console.error(error);
    } finally {
      setFetchingData(false);
    }
  }, [clientID]);

  const handleAntecipatePartial = useCallback(
    async (numeroParcela, idVenda, totalParcelas) => {
      setLoading(true);
      try {
        if (isNaN(Number(numeroParcela))) {
          throw new Error("Insira um número de parcelas válido.");
        }

        const res = await api.postLogin("antecipate_partial_sale", {
          idPortador: Number(clientID),
          idVenda: Number(idVenda),
          numeroParcela: Number(numeroParcela),
          totalParcelas: Number(totalParcelas),
        });

        handleForceUpdate();
        setLoading(false);
        setModalConfirmSales(false);
        setSelectedSaleId(0);
        onClose();
        notifySucces("Antecipação parcial de venda realizada com sucesso");
      } catch (error) {
        const err = error as TErrorElegileAntecipation;

        return notifyError(err.message);
      } finally {
        setLoading(false);
        setModalConfirmSales(false);
      }
    },
    [clientID, setSalesAntecipateData, notifySucces]
  );

  const handleAntecipateSaleTotal = useCallback(
    async (idCompra) => {
      setLoading(true);
      try {
        const res = await api.postLogin("antecipate_total_sale", {
          idPortador: Number(clientID),
          idVenda: Number(idCompra),
        });
        handleForceUpdate();
        setLoading(false);
        setModalConfirmSales(false);
        setSelectedSaleId(0);
        onClose();
        notifySucces("Antecipação Realizada com Sucesso!");
      } catch (error) {
        const err = error as TErrorElegileAntecipation;
        return notifyError(err.message);
      } finally {
        setLoading(false);
        setModalConfirmSales(false);
      }
    },
    [clientID, notifySucces]
  );

  const toggleSale = (id: number) => {
    setSelectedSaleId((prev) => (prev === id ? null : id));
  };

  const selectedSaleData = salesAntecipateData.find(
    (s) => s.idCompra === selectedSaleId
  );

  const antecipateTotal = () => {
    setType("total");
    setModalConfirmSales(true);
  };
  const antecipatePartial = () => {
    setType("partial");
    setModalConfirmSales(true);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    return formatDateDisplay(dateString);
  };

  useEffect(() => {
    if (isVisible && clientID) {
      fetchElegiblePurchase();
    }
  }, [isVisible]);

  return {
    salesAntecipateData,
    selectedSaleId,
    selectedSaleData,
    toggleSale,
    fetchingData,
    type,
    antecipatePartial,
    antecipateTotal,
    modalConfirmSales,
    setModalConfirmSales,
    handleAntecipatePartial,
    handleAntecipateSaleTotal,
    loading,
    formatDate,
    installmentsToAntecipate,
    setInstallmentsToAntecipate,
  };
};

export default useAntecipateSalesModal;
